import React from "react";
import { Card, Row, Col, Form, Button } from "react-bootstrap";
import ReactCountryFlag from "react-country-flag";
import { EditTeamRider, Rider, RiderInTeam } from "../../../../types/model";
import { LuDelete } from "react-icons/lu";
import RiderHistoryButton from "../RiderHistoryButton";

type PickAndRankTeamRiderCardProps = {
	rider: EditTeamRider;
	gender: "m" | "f";
	numberOfMen: number;
	numberOfWomen: number;
	updateRiderRank: (riderId: string, rank: number) => void;
	removeRiderFromTeam: (riderId: string) => void;
	getCountryISO2: (country: string) => string | undefined;
};

const PickAndRankTeamRiderCard: React.FC<PickAndRankTeamRiderCardProps> = ({
	rider,
	gender,
	numberOfMen,
	numberOfWomen,
	updateRiderRank,
	removeRiderFromTeam,
	getCountryISO2,
}) => {
	const countryISO2 = getCountryISO2(
		(rider.rider.country ?? "").toUpperCase().trim(),
	);

	return (
		<Card className="mb-1 bg-dark text-white border-0">
			<Card.Body className="py-2">
				<Row key={rider?.rider?._id ?? ""} className="align-items-center g-2">
					{/* Rank (full-width on xs, fixed area on md+) */}
					<Col xs={12} md={2} className="text-center">
						{/* d-grid makes it full width on mobile; turns into inline on md+ */}
						<div className="d-grid d-md-inline">
							<Form.Select
								size="sm"
								className="bg-dark text-white border-1 border-secondary text-center fw-bold"
								value={rider.rank}
								onChange={(e) =>
									updateRiderRank(
										rider.rider._id || "",
										parseInt(e.target.value, 10),
									)
								}
								// Compact width on md+; full width on mobile
								style={{ maxWidth: "6rem", margin: "0 auto" }}
							>
								{Array.from(
									{ length: gender === "m" ? numberOfMen : numberOfWomen },
									(_, i) => i + 1,
								).map((rank) => (
									<option key={rank} value={rank}>
										{rank}
									</option>
								))}
							</Form.Select>
						</div>
					</Col>

					{/* Name + Flag (center on mobile, left on md+) */}
					<Col xs={12} md={6} className="text-center text-md-start">
						{countryISO2 ? (
							<ReactCountryFlag
								countryCode={countryISO2}
								svg
								className="me-2"
								style={{ width: "2rem", height: "auto" }} // readable on mobile
							/>
						) : (
							<span className="me-2">{rider.rider.country}</span>
						)}
						<strong>{rider.rider.last_name}</strong> {rider.rider.first_name}
					</Col>

					{/* Remove button (full-width on mobile; right-aligned on md+) */}
					<Col xs={12} md={4} className="text-center text-md-end">
						{/* d-grid => full width on mobile; inline on md+ */}
						<div className="d-grid gap-1 d-md-inline">
							<RiderHistoryButton
								riderId={rider?.rider?._id}
								riderName={`${rider?.rider?.first_name || ""} ${rider?.rider?.last_name || ""}`.trim()}
								initialRider={rider?.rider}
								size="sm"
								className="px-4"
							/>
							<Button
								variant="outline-danger border-0"
								size="sm"
								className="px-5"
								onClick={() => removeRiderFromTeam(rider?.rider?._id ?? "")}
							>
								<LuDelete className="me-2 mb-1" />
								Remove
							</Button>
						</div>
					</Col>
				</Row>
			</Card.Body>
		</Card>
	);
};

export default PickAndRankTeamRiderCard;
